import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Moment } from "moment";
import { Subscription } from "rxjs";
import { ImpersonationService } from "../../../../auth/apigee/impersonation.service";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { ContractDetail, ExpiringContractsDetailServiceData, ForecastedExpiringContractsDetailService } from "../../services/contractsKpi/expiringContractsDetail.service";
import { BarChartData, BarChartDataset } from "../../../../dashboardCommon/components/cardContents/barChart.component";
import { ChartDropdownOption } from "../../../../dashboardCommon/components/cardContents/chartDropdown.component";
import { DateColumnDefinition, EmailColumnDefinition, NumberColumnDefinition, PhoneNumberColumnDefinition, TextColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";
import { ContractsDetailData } from "./contractsDetailData";

export class ExpiringContractsEvolutionData {
    public barChartData: BarChartData;
    public dates: Moment[];

    constructor(barChartData: BarChartData, dates: Moment[]) {
        this.barChartData = barChartData;
        this.dates = dates;
    }
}

@Component({
    selector: 'expiring-contracts-evolution-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class ExpiringContractsEvolutionKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<ForecastedExpiringContractsDetailService, ExpiringContractsDetailServiceData, ChartDropdownOption[], ContractsDetailData>
    implements OnDestroy {
    public getClassName() { return "ExpiringContractsEvolutionKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryDetailService: ForecastedExpiringContractsDetailService) {
        super(zone, logger, summaryDetailService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: ExpiringContractsDetailServiceData): ChartDropdownOption[] {
        if (data.contracts) {
            return this.generateOptions(data);
        }
        else return this.getDefaultSummaryData();
    }

    private extractConsultants(data: ContractDetail[]): { nif: string, name: string }[] {
        let nifs: { nif: string, name: string }[] = [];

        if (data) {
            data.forEach(x => {
                if (!nifs.find(y => x.nif == y.nif)) nifs.push({ nif: x.nif, name: x.name });
            })
        }

        return nifs;
    }

    private generateOption(optionName: string, data: ContractDetail[], filter: (x: ContractDetail) => boolean): ChartDropdownOption {
        let dateCounter = moment().startOf('day');
        let datasetData: { count: number, label: string, date: Moment }[] = new Array<{ count: number, label: string, date: Moment }>(7);
        datasetData.fill(null, 0, 7);
        datasetData.forEach((x, idx, arr) => {
            arr[idx] = { count: 0, label: dateCounter.date() + " " + this.getMonthName(dateCounter.month()), date: dateCounter }
            dateCounter = dateCounter.clone().add(1, "days");
        });

        data.filter(filter).reduce((acc, x) => {
            let contractDate = moment(x.endDate);
            let accElement = acc.find((x) => {
                return x.date.date() == contractDate.date() && x.date.month() == contractDate.month();
            });

            if (accElement) accElement.count++;

            return acc;
        }, datasetData);

        let result = new ExpiringContractsEvolutionData(new BarChartData(
            datasetData.map<string>(x => x.label),
            [
                new BarChartDataset("Contratos", datasetData.map<number>(x => x.count))
            ]
        ), datasetData.map<Moment>(x => x.date));

        let option = new ChartDropdownOption();
        option.text = optionName;
        option.value = result;

        return option;
    }

    private generateOptions(data: ExpiringContractsDetailServiceData): ChartDropdownOption[] {
        let consultants = this.extractConsultants(data.contracts);

        let global = this.generateOption("Todos", data.contracts, (x) => true);
        let otherOptions = consultants.map(x => this.generateOption(x.name, data.contracts, y => y.nif == x.nif))
            .sort((a, b) => (a.text ? a.text.toLowerCase() : "") < (b.text ? b.text.toLowerCase() : "") ? -1 : 1);

        return [global].concat(otherOptions);
    }

    private getMonthName(month: number): string {
        switch (month) {
            case 0: return "ene";
            case 1: return "feb";
            case 2: return "mar";
            case 3: return "abr";
            case 4: return "may";
            case 5: return "jun";
            case 6: return "jul";
            case 7: return "ago";
            case 8: return "sep";
            case 9: return "oct";
            case 10: return "nov";
            case 11: return "dic";
            default: return "";
        }
    }

    protected getDefaultSummaryData(): ChartDropdownOption[] {
        let dateCounter = moment().startOf('day');
        let datasetData: { count: number, label: string, date: Moment }[] = new Array<{ count: number, label: string, date: Moment }>(7);
        datasetData.fill(null, 0, 7);
        datasetData.forEach((x, idx, arr) => {
            arr[idx] = { count: 0, label: dateCounter.date() + " " + this.getMonthName(dateCounter.month()), date: dateCounter }
            dateCounter = dateCounter.clone().add(1, "days");
        });

        let result = new ExpiringContractsEvolutionData(new BarChartData(
            datasetData.map<string>(x => x.label),
            [
                new BarChartDataset("Contratos", datasetData.map<number>(x => x.count))
            ]
        ), datasetData.map<Moment>(x => x.date));

        let global = new ChartDropdownOption();
        global.text = "Todos";
        global.value = result;

        return [global];
    }

    protected extractUpdateTime(data: ExpiringContractsDetailServiceData): Date {
        return data.updateTimestamp;
    }

    protected processDetailData(data: ExpiringContractsDetailServiceData, args: any): ContractsDetailData {
        if (data.contracts) {
            if (args && args.filter && args.filter.name && args.filter.name!="Todos") {
                let columnDefinitions = [
                    new DateColumnDefinition("Fecha fin", (data) => moment(data).format("DD/MM/YYYY"), true),
                    new TextColumnDefinition("Cliente", null, true),
                    new TextColumnDefinition("Trabajador"),
                    new PhoneNumberColumnDefinition("Teléfono"),
                    new EmailColumnDefinition("Email"),
                    new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY")),
                    new NumberColumnDefinition("Tp. Contrato"),
                    new TextColumnDefinition("Consultor", null, true),
                    new TextColumnDefinition("Unidad", null, true),
                    new TextColumnDefinition("Nombre interlocutor", null, true),
                    new PhoneNumberColumnDefinition("Teléfono interlocutor"),
                ];

                let consultantNif = this.impersonationService.getNifSafe().toUpperCase();

                if (args && args.filter) {
                    if (args.filter.name) columnDefinitions[7].filterTerm = args.filter.name != "Todos" ? [args.filter.name] : null;
                    if (args.filter.date) { columnDefinitions[0].filterTerm = args.filter.date; }
                }

                let tabledata2 = [];
                let tabledata = [];
                for (let contract of data.contracts) {
                    if (contract.nif === consultantNif) {
                        tabledata.push([
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workername,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.laboralContractId,
                            contract.name,
                            contract.unitName,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                    else {
                        tabledata2.push([
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workername,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.laboralContractId,
                            contract.name,
                            contract.unitName,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                }
                let arrayOrdenado = tabledata.concat(tabledata2);
                return new ContractsDetailData(columnDefinitions, arrayOrdenado);
            } else {
                let columnDefinitions = [
                    new TextColumnDefinition("Consultor", null, true),
                    new TextColumnDefinition("Unidad", null, true),
                    new DateColumnDefinition("Fecha fin", (data) => moment(data).format("DD/MM/YYYY"), true),
                    new TextColumnDefinition("Cliente", null, true),
                    new TextColumnDefinition("Trabajador"),
                    new PhoneNumberColumnDefinition("Teléfono"),
                    new EmailColumnDefinition("Email"),
                    new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY")),
                    new NumberColumnDefinition("Tp. Contrato"),
                    new TextColumnDefinition("Nombre interlocutor", null, true),
                    new PhoneNumberColumnDefinition("Teléfono interlocutor"),
                ];

                let consultantNif = this.impersonationService.getNifSafe().toUpperCase();

                if (args && args.filter) {
                    if (args.filter.name) columnDefinitions[0].filterTerm = args.filter.name != "Todos" ? [args.filter.name] : null;
                    if (args.filter.date) { columnDefinitions[2].filterTerm = args.filter.date; }
                }

                let tabledata2 = [];
                let tabledata = [];
                for (let contract of data.contracts) {
                    if (contract.nif === consultantNif) {
                        tabledata.push([
                            contract.name,
                            contract.unitName,
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workername,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.laboralContractId,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                    else {
                        tabledata2.push([
                            contract.name,
                            contract.unitName,
                            moment(contract.endDate).startOf('day'),
                            contract.clientFiscalName,
                            contract.workername,
                            contract.workerPhone,
                            contract.workerEmail,
                            moment(contract.beginDate).startOf('day'),
                            contract.laboralContractId,
                            contract.contactName,
                            contract.contactPhone,
                        ]);
                    }
                }
                let arrayOrdenado = tabledata.concat(tabledata2);
                return new ContractsDetailData(columnDefinitions, arrayOrdenado);
            }
        }
        else return this.getDefaultDetailData();
    }

    protected getDefaultDetailData(): ContractsDetailData {
        let columnDefinitions = [
            new TextColumnDefinition("Consultor", null, true),
            new TextColumnDefinition("Unidad", null, true),
            new DateColumnDefinition("Fecha fin", (data) => moment(data).format("DD/MM/YYYY"), true),
            new TextColumnDefinition("Cliente", null, true),
            new TextColumnDefinition("Trabajador"),
            new PhoneNumberColumnDefinition("Teléfono"),
            new EmailColumnDefinition("Email"),
            new DateColumnDefinition("Fecha inicio", (data) => moment(data).format("DD/MM/YYYY")),
            new NumberColumnDefinition("Tp. Contrato"),
            new TextColumnDefinition("Nombre interlocutor", null, true),
            new PhoneNumberColumnDefinition("Teléfono interlocutor"),
        ];

        return new ContractsDetailData(columnDefinitions, [])
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }

    public mergeFilter(filter: any) {
        let detailProcessingArgs = this.detailProcessingArgs || {};
        detailProcessingArgs.filter = detailProcessingArgs.filter || {};
        detailProcessingArgs.filter = { ...detailProcessingArgs.filter, ...filter };
        this.detailProcessingArgs = detailProcessingArgs;
    }

    public showDetailEx(event: Event, prefilter: any) {
        this.mergeFilter(prefilter);
        this.showDetail(event);
    }
}
